import React from "react";
import {
  Building2,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Grid3X3,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Smile,
} from "lucide-react";
import {
  servicesTrustHeroSkylineTokens,
  servicesTrustHeroTokens,
  type ServicesTrustHeroSkylineToken,
} from "./ServicesTrustHero.tokens";

export type ServicesTrustHeroLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ServicesTrustHeroImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
};

export type ServicesTrustHeroActionIcon =
  | "contact"
  | "phone"
  | "mail"
  | "services"
  | React.ReactNode;

export type ServicesTrustHeroAction = {
  label: string;
  href: string;
  ariaLabel?: string;
  title?: string;
  target?: string;
  rel?: string;
  icon?: ServicesTrustHeroActionIcon;
};

export type ServicesTrustHeroBenefit =
  | string
  | {
      text: string;
    };

export type ServicesTrustHeroReview = {
  rating?: string;
  reviewCountText?: string;
  ctaLabel?: string;
  href?: string;
  ariaLabel?: string;
  target?: string;
  rel?: string;
  showStars?: boolean;
};

export type ServicesTrustHeroTrustIcon = "shield" | "clock" | "building" | "smile";

export type ServicesTrustHeroTrustItem = {
  icon: ServicesTrustHeroTrustIcon | React.ReactNode;
  title: string;
  text?: string;
};

export type ServicesTrustHeroSkyline =
  | ServicesTrustHeroSkylineToken
  | ServicesTrustHeroImage
  | "none"
  | false
  | null;

export type ServicesTrustHeroProps = {
  Link?: ServicesTrustHeroLinkComponent;
  skyline?: ServicesTrustHeroSkyline;
  image?: ServicesTrustHeroImage;
  badgeLabel?: string;
  badgeText?: string;
  title?: string;
  titleHighlight?: string;
  titleComponent?: "h1" | "h2" | "h3";
  lead?: string;
  description?: string;
  benefits?: ServicesTrustHeroBenefit[];
  primaryAction?: ServicesTrustHeroAction | false;
  secondaryAction?: ServicesTrustHeroAction | false;
  review?: ServicesTrustHeroReview | false;
  trustItems?: ServicesTrustHeroTrustItem[];
  accentColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  maxWidth?: number | string;
  className?: string;
  ariaLabel?: string;
};

const t = servicesTrustHeroTokens;

const servicesTrustHeroCss = `
.services-trust-hero {
  --sth-bg: ${t.colors.background};
  --sth-surface: ${t.colors.surface};
  --sth-text: ${t.colors.text};
  --sth-text-soft: ${t.colors.textSoft};
  --sth-text-muted: ${t.colors.textMuted};
  --sth-accent: ${t.colors.accent};
  --sth-accent-dark: ${t.colors.accentDark};
  --sth-border: ${t.colors.border};
  --sth-border-strong: ${t.colors.borderStrong};
  width: 100%;
  padding: 32px 16px;
  background: var(--sth-bg);
  color: var(--sth-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.services-trust-hero,
.services-trust-hero * {
  box-sizing: border-box;
}
.services-trust-hero-shell {
  position: relative;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--sth-border);
  border-radius: ${t.radius.shell}px;
  background:
    radial-gradient(circle at 75% 45%, rgba(237, 28, 36, 0.08), transparent 24%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.9) 58%, rgba(246, 248, 251, 0.96) 100%),
    var(--sth-surface);
  box-shadow: ${t.colors.shadow};
}
.services-trust-hero-skyline {
  position: absolute;
  z-index: 1;
  top: 52px;
  right: 18px;
  width: min(58%, 650px);
  height: auto;
  opacity: 0.62;
  pointer-events: none;
  user-select: none;
}
.services-trust-hero-inner {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 410px);
  grid-template-areas:
    "content media"
    "actions media"
    "review media"
    "trust trust";
  column-gap: 42px;
  row-gap: 22px;
  min-height: 690px;
  padding: 72px 40px 36px;
  align-items: start;
}
.services-trust-hero[data-has-image="false"] .services-trust-hero-inner {
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "content"
    "actions"
    "review"
    "trust";
  min-height: 0;
}
.services-trust-hero-content {
  grid-area: content;
  max-width: 650px;
  padding-top: 18px;
}
.services-trust-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  margin: 0 0 58px;
  padding: 14px 18px;
  border: 1px solid var(--sth-border);
  border-radius: ${t.radius.pill}px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: ${t.colors.softShadow};
}
.services-trust-hero-badge-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: var(--sth-accent);
}
.services-trust-hero-badge-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}
.services-trust-hero-badge-label {
  overflow-wrap: anywhere;
  color: var(--sth-accent);
  font-size: 11px;
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}
.services-trust-hero-badge-text {
  overflow-wrap: anywhere;
  color: var(--sth-text-soft);
  font-size: 13px;
  line-height: 1.22;
  font-weight: 600;
  letter-spacing: 0;
}
.services-trust-hero-title {
  max-width: 650px;
  margin: 0;
  color: var(--sth-text);
  font-size: 58px;
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: 0;
}
.services-trust-hero-title-highlight {
  display: block;
  color: var(--sth-accent);
}
.services-trust-hero-lead {
  max-width: 600px;
  margin: 22px 0 0;
  color: var(--sth-text);
  font-size: 19px;
  line-height: 1.45;
  font-weight: 800;
  letter-spacing: 0;
}
.services-trust-hero-description {
  max-width: 610px;
  margin: 18px 0 0;
  color: var(--sth-text-soft);
  font-size: 17px;
  line-height: 1.68;
  font-weight: 500;
  letter-spacing: 0;
}
.services-trust-hero-benefits {
  display: grid;
  gap: 14px;
  max-width: 580px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
}
.services-trust-hero-benefit {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  min-width: 0;
  color: var(--sth-text-soft);
  font-size: 15px;
  line-height: 1.38;
  font-weight: 600;
  letter-spacing: 0;
}
.services-trust-hero-benefit svg {
  flex: 0 0 auto;
  margin-top: 1px;
  color: var(--sth-accent);
}
.services-trust-hero-actions {
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  max-width: 640px;
}
.services-trust-hero-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 54px;
  min-width: 0;
  padding: 0 24px;
  border: 1px solid var(--sth-border-strong);
  border-radius: ${t.radius.button}px;
  color: var(--sth-text);
  background: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: 0;
  text-align: center;
  text-decoration: none;
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}
.services-trust-hero-action:hover,
.services-trust-hero-action:focus-visible {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--sth-accent) 32%, var(--sth-border-strong));
  box-shadow: ${t.colors.softShadow};
}
.services-trust-hero-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--sth-accent) 24%, transparent);
  outline-offset: 3px;
}
.services-trust-hero-action-primary {
  border-color: var(--sth-accent);
  background: var(--sth-accent);
  color: #ffffff;
}
.services-trust-hero-action-primary:hover,
.services-trust-hero-action-primary:focus-visible {
  border-color: var(--sth-accent-dark);
  background: var(--sth-accent-dark);
}
.services-trust-hero-action-icons {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
}
.services-trust-hero-review {
  grid-area: review;
  width: min(100%, 500px);
}
.services-trust-hero-review-card {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
  min-width: 0;
  padding: 20px 24px;
  border: 1px solid var(--sth-border);
  border-radius: ${t.radius.card}px;
  background: rgba(255, 255, 255, 0.94);
  color: inherit;
  box-shadow: ${t.colors.softShadow};
  text-decoration: none;
}
a.services-trust-hero-review-card:hover,
a.services-trust-hero-review-card:focus-visible {
  border-color: color-mix(in srgb, ${t.colors.blue} 28%, var(--sth-border));
}
a.services-trust-hero-review-card:focus-visible {
  outline: 3px solid rgba(26, 115, 232, 0.2);
  outline-offset: 3px;
}
.services-trust-hero-google-logo {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
}
.services-trust-hero-review-copy {
  display: grid;
  min-width: 0;
  gap: 8px;
}
.services-trust-hero-rating-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}
.services-trust-hero-rating {
  color: var(--sth-text);
  font-size: 24px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.services-trust-hero-stars {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${t.colors.gold};
}
.services-trust-hero-review-text {
  color: var(--sth-text-soft);
  font-size: 13px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0;
}
.services-trust-hero-review-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: max-content;
  color: ${t.colors.blue};
  font-size: 14px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0;
}
.services-trust-hero-media {
  grid-area: media;
  align-self: end;
  width: 100%;
  min-width: 0;
  margin-top: 96px;
}
.services-trust-hero-image {
  display: block;
  width: 100%;
  aspect-ratio: 0.76;
  object-fit: cover;
  object-position: center;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: ${t.radius.card}px;
  box-shadow: 0 22px 54px rgba(7, 20, 47, 0.18);
}
.services-trust-hero-trust {
  grid-area: trust;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin-top: 10px;
  padding-top: 24px;
  border-top: 1px solid var(--sth-border);
}
.services-trust-hero-trust-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-width: 0;
  min-height: 58px;
  padding: 0 18px;
  border-left: 1px solid var(--sth-border);
}
.services-trust-hero-trust-item:first-child {
  border-left: 0;
}
.services-trust-hero-trust-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: var(--sth-text-soft);
}
.services-trust-hero-trust-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}
.services-trust-hero-trust-title {
  overflow-wrap: anywhere;
  color: var(--sth-text);
  font-size: 13px;
  line-height: 1.25;
  font-weight: 900;
  letter-spacing: 0;
}
.services-trust-hero-trust-text {
  overflow-wrap: anywhere;
  color: var(--sth-text-muted);
  font-size: 12px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: 0;
}
@media (max-width: 980px) {
  .services-trust-hero {
    padding: 24px 14px;
  }
  .services-trust-hero-shell {
    max-width: 520px;
  }
  .services-trust-hero-skyline {
    top: 34px;
    right: 20px;
    width: 38%;
    opacity: 0.44;
  }
  .services-trust-hero-inner,
  .services-trust-hero[data-has-image="false"] .services-trust-hero-inner {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "content"
      "review"
      "media"
      "actions"
      "trust";
    min-height: 0;
    padding: 32px 24px 26px;
    row-gap: 18px;
  }
  .services-trust-hero-content {
    max-width: none;
    padding-top: 0;
  }
  .services-trust-hero-badge {
    margin-bottom: 30px;
    padding: 12px 14px;
  }
  .services-trust-hero-title {
    max-width: 390px;
    font-size: 36px;
    line-height: 1.08;
  }
  .services-trust-hero-lead {
    margin-top: 14px;
    font-size: 15px;
    line-height: 1.48;
  }
  .services-trust-hero-description {
    margin-top: 13px;
    font-size: 15px;
    line-height: 1.62;
  }
  .services-trust-hero-benefits {
    gap: 13px;
    margin-top: 22px;
  }
  .services-trust-hero-benefit {
    font-size: 14px;
  }
  .services-trust-hero-review {
    width: 100%;
  }
  .services-trust-hero-media {
    margin-top: 0;
  }
  .services-trust-hero-image {
    aspect-ratio: 1.78;
  }
  .services-trust-hero-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    gap: 10px;
  }
  .services-trust-hero-action {
    width: 100%;
    min-height: 50px;
  }
  .services-trust-hero-trust {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 4px;
    padding-top: 18px;
  }
  .services-trust-hero-trust-item {
    flex-direction: column;
    gap: 8px;
    justify-content: flex-start;
    min-height: 104px;
    padding: 0 10px;
    text-align: center;
  }
  .services-trust-hero-trust-title {
    font-size: 11px;
    line-height: 1.22;
  }
  .services-trust-hero-trust-text {
    font-size: 10px;
    line-height: 1.2;
  }
}
@media (max-width: 520px) {
  .services-trust-hero {
    padding: 16px 10px;
  }
  .services-trust-hero-inner,
  .services-trust-hero[data-has-image="false"] .services-trust-hero-inner {
    padding: 28px 16px 20px;
    row-gap: 15px;
  }
  .services-trust-hero-skyline {
    top: 28px;
    right: 10px;
    width: 44%;
  }
  .services-trust-hero-badge {
    gap: 9px;
    margin-bottom: 28px;
    padding: 10px 11px;
  }
  .services-trust-hero-badge-icon {
    width: 23px;
    height: 23px;
  }
  .services-trust-hero-badge-label {
    font-size: 9px;
  }
  .services-trust-hero-badge-text {
    font-size: 11px;
  }
  .services-trust-hero-title {
    max-width: 320px;
    font-size: 28px;
    line-height: 1.1;
  }
  .services-trust-hero-lead {
    font-size: 13px;
    line-height: 1.58;
  }
  .services-trust-hero-description {
    font-size: 13px;
    line-height: 1.58;
  }
  .services-trust-hero-benefits {
    gap: 12px;
    margin-top: 18px;
  }
  .services-trust-hero-benefit {
    gap: 10px;
    font-size: 12px;
    line-height: 1.42;
  }
  .services-trust-hero-review-card {
    gap: 12px;
    padding: 16px 14px;
  }
  .services-trust-hero-google-logo {
    width: 38px;
    height: 38px;
  }
  .services-trust-hero-rating {
    font-size: 20px;
  }
  .services-trust-hero-stars svg {
    width: 15px;
    height: 15px;
  }
  .services-trust-hero-review-text {
    font-size: 11px;
  }
  .services-trust-hero-review-link {
    font-size: 11px;
  }
  .services-trust-hero-image {
    aspect-ratio: 1.62;
  }
  .services-trust-hero-action {
    min-height: 48px;
    padding: 0 14px;
    font-size: 13px;
  }
  .services-trust-hero-trust {
    padding-top: 16px;
  }
  .services-trust-hero-trust-item {
    gap: 7px;
    min-height: 94px;
    padding: 0 5px;
  }
  .services-trust-hero-trust-icon svg {
    width: 22px;
    height: 22px;
  }
  .services-trust-hero-trust-title {
    font-size: 9px;
    line-height: 1.2;
  }
  .services-trust-hero-trust-text {
    font-size: 8px;
    line-height: 1.2;
  }
}
@media (max-width: 360px) {
  .services-trust-hero-inner,
  .services-trust-hero[data-has-image="false"] .services-trust-hero-inner {
    padding-left: 12px;
    padding-right: 12px;
  }
  .services-trust-hero-trust-item {
    padding-left: 3px;
    padding-right: 3px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getImageSrc(image?: ServicesTrustHeroImage): string {
  if (!image) return "";
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image?: ServicesTrustHeroImage, fallback = ""): string {
  if (!image) return fallback;
  return image.alt || image.title || fallback;
}

function resolveSkyline(skyline: ServicesTrustHeroSkyline): ServicesTrustHeroImage | undefined {
  if (!skyline || skyline === "none") return undefined;

  if (typeof skyline === "string") {
    return servicesTrustHeroSkylineTokens[skyline];
  }

  return skyline;
}

function renderHighlightedTitle(title: string, highlight?: string) {
  if (!highlight) return title;

  const highlightIndex = title.toLowerCase().indexOf(highlight.toLowerCase());

  if (highlightIndex === -1) return title;

  const before = title.slice(0, highlightIndex);
  const match = title.slice(highlightIndex, highlightIndex + highlight.length);
  const after = title.slice(highlightIndex + highlight.length);

  return (
    <>
      {before}
      <span className="services-trust-hero-title-highlight">{match}</span>
      {after}
    </>
  );
}

function getBenefitText(benefit: ServicesTrustHeroBenefit): string {
  return typeof benefit === "string" ? benefit : benefit.text;
}

function GoogleLogo() {
  return (
    <svg className="services-trust-hero-google-logo" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.91-6.91C35.91 2.44 30.37 0 24 0 14.82 0 6.73 5.15 2.69 12.68l8.06 6.26C12.7 13.16 17.9 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.14 24.5c0-1.64-.15-3.21-.43-4.72H24v9h12.44c-.54 2.91-2.18 5.38-4.64 7.05l7.19 5.59C43.98 36.91 46.14 31.23 46.14 24.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.75 28.94A14.5 14.5 0 019.5 24c0-1.72.3-3.37.84-4.94l-8.06-6.26A23.93 23.93 0 000 24c0 3.87.93 7.53 2.58 10.74l8.17-5.8z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.92-2.13 15.9-5.79l-7.19-5.59c-2 1.35-4.56 2.15-8.71 2.15-6.1 0-11.3-3.66-13.25-8.84l-8.17 5.8C6.73 42.85 14.82 48 24 48z"
      />
    </svg>
  );
}

function Star() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5l2.93 5.94 6.56.95-4.75 4.63 1.12 6.53L12 17.77 6.14 20.55l1.12-6.53L2.5 9.39l6.56-.95L12 2.5z" />
    </svg>
  );
}

function ActionIcon({ icon }: { icon?: ServicesTrustHeroActionIcon }) {
  if (!icon) return null;

  if (React.isValidElement(icon)) {
    return <span className="services-trust-hero-action-icons">{icon}</span>;
  }

  if (icon === "contact") {
    return (
      <span className="services-trust-hero-action-icons" aria-hidden="true">
        <Phone size={19} strokeWidth={2.5} />
        <Mail size={19} strokeWidth={2.5} />
      </span>
    );
  }

  if (icon === "phone") {
    return (
      <span className="services-trust-hero-action-icons" aria-hidden="true">
        <Phone size={19} strokeWidth={2.5} />
      </span>
    );
  }

  if (icon === "mail") {
    return (
      <span className="services-trust-hero-action-icons" aria-hidden="true">
        <Mail size={19} strokeWidth={2.5} />
      </span>
    );
  }

  if (icon === "services") {
    return (
      <span className="services-trust-hero-action-icons" aria-hidden="true">
        <Grid3X3 size={18} strokeWidth={2.4} />
      </span>
    );
  }

  return null;
}

function TrustIcon({ icon }: { icon: ServicesTrustHeroTrustItem["icon"] }) {
  if (React.isValidElement(icon)) {
    return <span className="services-trust-hero-trust-icon">{icon}</span>;
  }

  const props = { size: 34, strokeWidth: 1.8, "aria-hidden": true };

  if (icon === "clock") {
    return (
      <span className="services-trust-hero-trust-icon">
        <Clock3 {...props} />
      </span>
    );
  }

  if (icon === "building") {
    return (
      <span className="services-trust-hero-trust-icon">
        <Building2 {...props} />
      </span>
    );
  }

  if (icon === "smile") {
    return (
      <span className="services-trust-hero-trust-icon">
        <Smile {...props} />
      </span>
    );
  }

  return (
    <span className="services-trust-hero-trust-icon">
      <ShieldCheck {...props} />
    </span>
  );
}

function HeroAction({
  action,
  variant,
  Link,
}: {
  action: ServicesTrustHeroAction;
  variant: "primary" | "secondary";
  Link?: ServicesTrustHeroLinkComponent;
}) {
  const LinkComponent = (Link || "a") as React.ElementType;
  const rel = action.rel || (action.target === "_blank" ? "noopener noreferrer" : undefined);

  return (
    <LinkComponent
      href={action.href}
      className={`services-trust-hero-action services-trust-hero-action-${variant}`}
      aria-label={action.ariaLabel}
      title={action.title}
      target={action.target}
      rel={rel}
    >
      <ActionIcon icon={action.icon} />
      <span>{action.label}</span>
    </LinkComponent>
  );
}

function ReviewCard({ review }: { review: Required<Pick<ServicesTrustHeroReview, "showStars">> & ServicesTrustHeroReview }) {
  const cardContent = (
    <>
      <GoogleLogo />
      <span className="services-trust-hero-review-copy">
        <span className="services-trust-hero-rating-row">
          {review.rating ? <span className="services-trust-hero-rating">{review.rating}</span> : null}
          {review.showStars ? (
            <span className="services-trust-hero-stars" aria-label={`${review.rating || "Google"} star rating`}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Star key={index} />
              ))}
            </span>
          ) : null}
        </span>
        {review.reviewCountText ? (
          <span className="services-trust-hero-review-text">{review.reviewCountText}</span>
        ) : null}
        {review.ctaLabel ? (
          <span className="services-trust-hero-review-link">
            {review.ctaLabel}
            <ExternalLink size={13} strokeWidth={2.4} aria-hidden="true" />
          </span>
        ) : null}
      </span>
    </>
  );

  if (!review.href) {
    return <div className="services-trust-hero-review-card">{cardContent}</div>;
  }

  return (
    <a
      className="services-trust-hero-review-card"
      href={review.href}
      aria-label={review.ariaLabel || review.ctaLabel}
      target={review.target}
      rel={review.rel || (review.target === "_blank" ? "noopener noreferrer" : undefined)}
    >
      {cardContent}
    </a>
  );
}

export default function ServicesTrustHero({
  Link,
  skyline = t.defaults.skyline,
  image,
  badgeLabel = t.defaults.badgeLabel,
  badgeText = t.defaults.badgeText,
  title = t.defaults.title,
  titleHighlight = t.defaults.titleHighlight,
  titleComponent = "h1",
  lead = t.defaults.lead,
  description = t.defaults.description,
  benefits = [...t.defaults.benefits],
  primaryAction = t.defaults.primaryAction,
  secondaryAction = t.defaults.secondaryAction,
  review = t.defaults.review,
  trustItems = [...t.defaults.trustItems] as ServicesTrustHeroTrustItem[],
  accentColor = t.colors.accent,
  backgroundColor = t.colors.background,
  surfaceColor = t.colors.surface,
  maxWidth = t.sizes.maxWidth,
  className = "",
  ariaLabel,
}: ServicesTrustHeroProps) {
  const resolvedSkyline = resolveSkyline(skyline);
  const skylineSrc = getImageSrc(resolvedSkyline);
  const imageSrc = getImageSrc(image);
  const HeadingTag = titleComponent;
  const resolvedReview =
    review === false
      ? undefined
      : {
          ...t.defaults.review,
          showStars: true,
          ...(review || {}),
        };
  const resolvedPrimaryAction = primaryAction || undefined;
  const resolvedSecondaryAction = secondaryAction || undefined;
  const hasActions = Boolean(resolvedPrimaryAction || resolvedSecondaryAction);
  const hasBenefits = Boolean(benefits.length);
  const hasTrustItems = Boolean(trustItems.length);

  return (
    <section
      className={`services-trust-hero ${className}`}
      aria-label={ariaLabel || title}
      data-has-image={imageSrc ? "true" : "false"}
      style={
        {
          "--sth-bg": backgroundColor,
          "--sth-surface": surfaceColor,
          "--sth-accent": accentColor,
        } as React.CSSProperties
      }
    >
      <style>{servicesTrustHeroCss}</style>

      <div className="services-trust-hero-shell" style={{ maxWidth: getCssSize(maxWidth) }}>
        {skylineSrc ? (
          <img
            className="services-trust-hero-skyline"
            src={skylineSrc}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
          />
        ) : null}

        <div className="services-trust-hero-inner">
          <div className="services-trust-hero-content">
            {(badgeLabel || badgeText) ? (
              <div className="services-trust-hero-badge">
                <span className="services-trust-hero-badge-icon" aria-hidden="true">
                  <MapPin size={30} fill="currentColor" strokeWidth={0} />
                </span>
                <span className="services-trust-hero-badge-copy">
                  {badgeLabel ? <span className="services-trust-hero-badge-label">{badgeLabel}</span> : null}
                  {badgeText ? <span className="services-trust-hero-badge-text">{badgeText}</span> : null}
                </span>
              </div>
            ) : null}

            <HeadingTag className="services-trust-hero-title">
              {renderHighlightedTitle(title, titleHighlight)}
            </HeadingTag>

            {lead ? <p className="services-trust-hero-lead">{lead}</p> : null}
            {description ? <p className="services-trust-hero-description">{description}</p> : null}

            {hasBenefits ? (
              <ul className="services-trust-hero-benefits">
                {benefits.map((benefit, index) => (
                  <li className="services-trust-hero-benefit" key={`${getBenefitText(benefit)}-${index}`}>
                    <CheckCircle2 size={18} strokeWidth={2.4} aria-hidden="true" />
                    <span>{getBenefitText(benefit)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {hasActions ? (
            <div className="services-trust-hero-actions">
              {resolvedPrimaryAction ? <HeroAction action={resolvedPrimaryAction} variant="primary" Link={Link} /> : null}
              {resolvedSecondaryAction ? (
                <HeroAction action={resolvedSecondaryAction} variant="secondary" Link={Link} />
              ) : null}
            </div>
          ) : null}

          {resolvedReview ? (
            <div className="services-trust-hero-review">
              <ReviewCard review={resolvedReview} />
            </div>
          ) : null}

          {imageSrc ? (
            <div className="services-trust-hero-media">
              <img
                className="services-trust-hero-image"
                src={imageSrc}
                alt={getImageAlt(image, title)}
                width={image?.width}
                height={image?.height}
                loading={image?.loading || "lazy"}
                decoding="async"
              />
            </div>
          ) : null}

          {hasTrustItems ? (
            <div className="services-trust-hero-trust" aria-label="Trust signals">
              {trustItems.map((item, index) => (
                <div className="services-trust-hero-trust-item" key={`${item.title}-${index}`}>
                  <TrustIcon icon={item.icon} />
                  <span className="services-trust-hero-trust-copy">
                    <span className="services-trust-hero-trust-title">{item.title}</span>
                    {item.text ? <span className="services-trust-hero-trust-text">{item.text}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export {
  servicesTrustHeroSkylineTokens,
  servicesTrustHeroTokens,
  type ServicesTrustHeroSkylineToken,
};
